import React from 'react';
import { Button, Checkbox, Input, Loader } from 'freemium-ui';
import { uniq, uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { ColumnSettingsType, FormServFieldType } from '../../../shared/types';
import Icon from '../../../shared/components/Icon';
import Reorder from '../../../shared/components/Reorder';
import {
  COLUMNS_HIDE_ORDERING_KEY,
  HIDE_EXPORT_COLUUMNS_FIELDS,
  TEMPORARY_HIDDEN_FIELDS,
} from '../../../shared/constants';
import {
  Center,
  FormActions,
  FormContainer,
  FormContent,
  FormField,
  FormHeader,
} from '../../../shared/styles';
import { Adornment, FieldRow } from '../../styles';
import {
  ColumnsHolder,
  ScrollContainer,
  SortItem,
  SplitScreen,
} from '../styles';

type ExportColumnsFormProps = {
  toggleDrawer: (flag: boolean) => void;
  fields?: FormServFieldType[];
  name?: string;
  columnOrder?: string[];
  columnSettings?: ColumnSettingsType;
  hiddenColumns?: string[];
  isExportingContacts?: boolean;
  isFetching?: boolean;
  onSubmit?: (data: {
    localColumnOrder: string[];
    localHiddenColumns: string[];
  }) => void;
};

const ExportColumnsForm = (props: ExportColumnsFormProps) => {
  const { t } = useTranslation();
  const {
    toggleDrawer = () => {},
    onSubmit,
    name = 'Export Data',
    isFetching = false,
    isExportingContacts = false,
    fields = [],
    columnSettings = {},
    columnOrder = [],
    hiddenColumns = [],
  } = props;

  const scrollRef: React.MutableRefObject<HTMLDivElement | undefined> =
    React.useRef();

  const [filteredFields, setFilteredFields] = React.useState<
    FormServFieldType[]
  >([]);
  const [sortedFields, setSortedFields] = React.useState<FormServFieldType[]>(
    [],
  );
  const [localHiddenColumns, setLocalHiddenColumns] = React.useState<string[]>(
    [],
  );
  const [searchText, setSearchText] = React.useState<string>('');

  const { allColumns, disabledFields, hiddenFields, localFields } =
    React.useMemo<{
      allColumns: Record<string, FormServFieldType>;
      disabledFields: FormServFieldType[];
      hiddenFields: FormServFieldType[];
      localFields: FormServFieldType[];
    }>(() => {
      const newAllColumns: Record<string, FormServFieldType> = {};
      fields.forEach((field: FormServFieldType) => {
        newAllColumns[field[COLUMNS_HIDE_ORDERING_KEY]] = field;
      });
      const newDisabledFields =
        fields.filter(
          (field: FormServFieldType) =>
            field &&
            field.name &&
            columnSettings &&
            columnSettings[field.name] &&
            columnSettings[field.name].disableReordering,
        ) || [];
      const newhiddenFields =
        fields.filter((f) =>
          [...TEMPORARY_HIDDEN_FIELDS, ...HIDE_EXPORT_COLUUMNS_FIELDS].includes(
            f.name,
          ),
        ) || [];
      const newLocalFields =
        fields.filter(
          (f) =>
            ![
              ...TEMPORARY_HIDDEN_FIELDS,
              ...HIDE_EXPORT_COLUUMNS_FIELDS,
              ...newDisabledFields.map((f) => f.name),
            ].includes(f.name),
        ) || [];
      return {
        allColumns: newAllColumns,
        disabledFields: newDisabledFields,
        hiddenFields: newhiddenFields,
        localFields: newLocalFields,
      };
    }, [fields]);

  React.useEffect(() => {
    const newFilteredFields = localFields.filter((localField) =>
      localField.label?.toLowerCase().includes(searchText.toLowerCase()),
    );
    setFilteredFields(newFilteredFields);
  }, [searchText]);

  React.useEffect(() => {
    setFilteredFields(localFields);
  }, [localFields]);

  React.useEffect(() => {
    setSortedFields(
      columnOrder
        .filter(
          (cO) =>
            ![
              ...hiddenColumns,
              ...hiddenFields.map((dF) => dF[COLUMNS_HIDE_ORDERING_KEY]),
              ...disabledFields.map((dF) => dF[COLUMNS_HIDE_ORDERING_KEY]),
            ].includes(cO),
        )
        .map((cId) => allColumns[cId]),
    );
    setLocalHiddenColumns(
      uniq([
        ...hiddenColumns,
        // We need hidden field id/name based on COLUMNS_HIDE_ORDERING_KEY
        ...hiddenFields.map((h) => h[COLUMNS_HIDE_ORDERING_KEY]),
      ]),
    );
  }, [columnOrder, hiddenColumns]);

  const removeFieldFromSortedList = (fieldToRemove: FormServFieldType) => {
    setLocalHiddenColumns(
      uniq([...localHiddenColumns, fieldToRemove[COLUMNS_HIDE_ORDERING_KEY]]),
    );
    setSortedFields((old) => old.filter((o) => o.id !== fieldToRemove.id));
  };

  const addFieldToSortedList = (fieldToAdd: FormServFieldType) => {
    setLocalHiddenColumns(
      uniq(
        localHiddenColumns.filter(
          (cId: string) => cId !== fieldToAdd[COLUMNS_HIDE_ORDERING_KEY],
        ),
      ),
    );
    setSortedFields((old) => [...old, fieldToAdd]);
    setTimeout(() => {
      if (scrollRef && scrollRef.current)
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 100);
  };

  return isExportingContacts ? (
    <Center>
      <Loader />
      <div className="text">{t('contacts.export.exporting')}</div>
      <div className="sub-text">{t('contacts.export.sub_text')}</div>
    </Center>
  ) : (
    <FormContainer>
      <FormHeader hideBorder>
        <div className="heading">{name}</div>
      </FormHeader>
      <FormContent>
        <ColumnsHolder>
          <FormField>
            <div className="heading">
              {t('contacts.export.heading', {
                length: `${sortedFields.length + disabledFields.length}`,
              })}
            </div>
            {/* <DisplayBox className="info">
              <div>
                <Icon fill="#264966" width="16" height="16" glyph="info" />
              </div>
              <div className="divider" />
              <div className="text-content">
                Contacts in this view can be exported
              </div>
            </DisplayBox> */}
          </FormField>
          <SplitScreen>
            <div className="left-pane">
              <FormField>
                <div className="heading">
                  {t('contacts.edit_column_drawer.fields')}
                </div>
              </FormField>
              <FormField>
                <Input
                  value={searchText}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchText(event.target.value)
                  }
                  placeholder={`${t('forms.placeholders.search_fields')}`}
                  startAdornment={
                    <Adornment>
                      <Icon width="17" height="17" glyph="search" />
                    </Adornment>
                  }
                  inline
                />
              </FormField>
              <FormField>
                <Checkbox
                  checked={localHiddenColumns.length === hiddenFields.length}
                  name="selectAll"
                  id="selectAll"
                  onChange={(e: any, checked: boolean) => {
                    if (checked) {
                      const newhiddenCols = hiddenFields.map(
                        (h: FormServFieldType) => h[COLUMNS_HIDE_ORDERING_KEY],
                      );
                      setSortedFields((old) =>
                        uniqBy(
                          [
                            ...old,
                            // change to filteredFields
                            ...localFields.filter((field) =>
                              localHiddenColumns.includes(
                                field[COLUMNS_HIDE_ORDERING_KEY],
                              ),
                            ),
                          ],
                          COLUMNS_HIDE_ORDERING_KEY,
                        ),
                      );
                      setLocalHiddenColumns(newhiddenCols);
                    } else {
                      setSortedFields([]);
                      setLocalHiddenColumns(
                        uniq(
                          [...hiddenFields, ...localFields].map(
                            (h: FormServFieldType) =>
                              h[COLUMNS_HIDE_ORDERING_KEY],
                          ),
                        ),
                      );
                    }
                  }}
                >
                  {t('contacts.edit_column_drawer.select_all_fields')}
                </Checkbox>
              </FormField>
              {disabledFields.map((disabledField) => (
                <FieldRow key={disabledField.id} className="pd-left">
                  <Checkbox
                    checked
                    name={disabledField.name}
                    disabled
                    id={disabledField.id}
                  >
                    <span className="cbx-text">{disabledField.label}</span>
                  </Checkbox>
                </FieldRow>
              ))}
              <ScrollContainer>
                {filteredFields.map((filteredField) => (
                  <FieldRow key={filteredField.id}>
                    <Checkbox
                      checked={
                        !localHiddenColumns.includes(
                          filteredField[COLUMNS_HIDE_ORDERING_KEY],
                        )
                      }
                      onChange={(e: any, checked: boolean) => {
                        if (checked) {
                          addFieldToSortedList(filteredField);
                        } else {
                          removeFieldFromSortedList(filteredField);
                        }
                      }}
                      name={filteredField.name}
                      id={filteredField.id}
                    >
                      <span className="cbx-text">{filteredField.label}</span>
                    </Checkbox>
                  </FieldRow>
                ))}
              </ScrollContainer>
            </div>
            <div className="right-pane">
              <FormField>
                <div className="heading">
                  {t('contacts.edit_column_drawer.selected_fields')}
                </div>
              </FormField>
              {disabledFields.map((field: FormServFieldType) => (
                <SortItem className="disabled" key={field.id}>
                  {field.label}
                </SortItem>
              ))}
              <ScrollContainer ref={scrollRef}>
                <Reorder
                  onReorder={(newSortedFields) =>
                    setSortedFields(newSortedFields)
                  }
                  customRender={(params: FormServFieldType) => (
                    <SortItem key={params.id}>
                      <span>{params.label}</span>
                      <div
                        className="close-action"
                        onClick={() => removeFieldFromSortedList(params)}
                      >
                        <Icon
                          glyph="close"
                          height={20}
                          width={20}
                          viewBox="0 0 20 20"
                        />
                      </div>
                    </SortItem>
                  )}
                  items={sortedFields}
                />
              </ScrollContainer>
            </div>
          </SplitScreen>
        </ColumnsHolder>
      </FormContent>
      <FormActions>
        <div />
        <div>
          <Button
            inline
            htmlType="reset"
            type="secondary"
            onClick={() => toggleDrawer(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            inline
            disabled={isFetching}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              if (onSubmit)
                onSubmit({
                  localColumnOrder: uniq(
                    [...disabledFields, ...sortedFields, ...fields].map(
                      (field) => field[COLUMNS_HIDE_ORDERING_KEY],
                    ),
                  ),
                  localHiddenColumns: uniq(localHiddenColumns),
                });
            }}
          >
            {t('export')}
          </Button>
        </div>
      </FormActions>
    </FormContainer>
  );
};

export default ExportColumnsForm;
